// src/pages/ScoutProfile.js
import React, { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Textarea } from '../components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card'
import { Label } from '../components/ui/label'
import { toast } from 'sonner'
import { ArrowLeft, Star, AlertCircle, CheckCircle, Camera, Upload } from 'lucide-react'
import DarkModeToggle from '../components/DarkModeToggle'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`

const socialLinkFields = [
  { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/theirprofile' },
  { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/theirprofile' },
  { key: 'twitter', label: 'X (Twitter)', placeholder: 'https://x.com/theirprofile' },
  { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/in/theirprofile' },
  { key: 'reddit', label: 'Reddit', placeholder: 'https://reddit.com/u/theirprofile' },
  { key: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/@theirprofile' },
  { key: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@theirprofile' },
  { key: 'website', label: 'Website', placeholder: 'https://theirwebsite.com' },
]

export default function ScoutProfile() {
  const navigate = useNavigate()
  const { token } = useAuth()
  const { isDarkMode } = useTheme()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [profileImage, setProfileImage] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const fileInputRef = useRef(null)
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    bio: '',
    facebook: '',
    instagram: '',
    twitter: '',
    linkedin: '',
    reddit: '',
    youtube: '',
    tiktok: '',
    website: '',
  })

  const countValidLinks = () => {
    return socialLinkFields.filter(f => formData[f.key]?.trim()).length
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image too large. Max 5MB allowed.')
        return
      }
      setProfileImage(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.name.trim()) {
      toast.error('Please enter a name')
      return
    }
    
    if (!formData.username.trim()) {
      toast.error('Please enter a username')
      return
    }
    
    if (countValidLinks() < 3) {
      toast.error('Please provide at least 3 social links')
      return
    }

    setIsSubmitting(true)
    try {
      const social_links = {}
      socialLinkFields.forEach(f => {
        if (formData[f.key]?.trim()) {
          social_links[f.key] = formData[f.key].trim()
        }
      })

      // Include profile_image if uploaded
      let profileImageData = null
      if (profileImage) {
        const reader = new FileReader()
        profileImageData = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result)
          reader.readAsDataURL(profileImage)
        })
      }

      await axios.post(
        `${API}/profiles/claimable`,
        {
          name: formData.name.trim(),
          username: formData.username.trim().toLowerCase(),
          bio: formData.bio?.trim() || null,
          social_links,
          profile_image: profileImageData
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      toast.success('Claimable profile created! You will receive 10% of their earned respect when they claim it.')
      navigate('/home')
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create claimable profile')
    } finally {
      setIsSubmitting(false)
    }
  }

  const validLinkCount = countValidLinks()

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50'}`}>
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className={isDarkMode ? 'text-gray-300 hover:text-white' : ''}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <DarkModeToggle />
        </div>

        {/* Main Card */}
        <Card className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white/90'} backdrop-blur-sm`}>
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className={`p-4 rounded-full ${isDarkMode ? 'bg-yellow-900/50' : 'bg-yellow-100'}`}>
                <Star className={`w-8 h-8 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
              </div>
            </div>
            <CardTitle className={`text-2xl ${isDarkMode ? 'text-gray-100' : ''}`} style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Create a Claimable Profile
            </CardTitle>
            <CardDescription className={isDarkMode ? 'text-gray-400' : ''}>
              Scout someone who deserves recognition. You'll earn <strong className="text-yellow-600">10% of all respect</strong> they receive when they claim this profile!
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Info Banner */}
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-yellow-900/30 border-yellow-700' : 'bg-yellow-50 border-yellow-200'} border`}>
                <div className="flex items-start gap-3">
                  <AlertCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
                  <div className={`text-sm ${isDarkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                    <p className="font-semibold mb-1">Requirements:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Minimum <strong>3 social links</strong> required</li>
                      <li>Links must not belong to any existing profile</li>
                      <li>Profile will be marked as "Unclaimed" until verified</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Profile Picture Upload */}
              <div className="flex flex-col items-center">
                <Label className={`mb-2 ${isDarkMode ? 'text-gray-200' : ''}`}>
                  Profile Picture (Optional)
                </Label>
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className={`w-24 h-24 sm:w-32 sm:h-32 rounded-full border-2 border-dashed cursor-pointer flex items-center justify-center overflow-hidden ${
                    isDarkMode ? 'border-gray-600 hover:border-yellow-500' : 'border-gray-300 hover:border-yellow-500'
                  } transition-colors`}
                >
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-center">
                      <Camera className={`w-8 h-8 mx-auto ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                      <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Upload</span>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                  data-testid="scout-profile-image-input"
                />
                {imagePreview && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => { setProfileImage(null); setImagePreview(null); }}
                    className="mt-2 text-red-500"
                  >
                    Remove
                  </Button>
                )}
              </div>

              {/* Basic Info */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name" className={isDarkMode ? 'text-gray-200' : ''}>
                    Full Name *
                  </Label>
                  <Input
                    id="name"
                    data-testid="scout-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`mt-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : ''}`}
                    placeholder="Their full name"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="username" className={isDarkMode ? 'text-gray-200' : ''}>
                    Username *
                  </Label>
                  <Input
                    id="username"
                    data-testid="scout-username-input"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                    className={`mt-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : ''}`}
                    placeholder="their_username"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="bio" className={isDarkMode ? 'text-gray-200' : ''}>
                    Bio (Optional)
                  </Label>
                  <Textarea
                    id="bio"
                    data-testid="scout-bio-input"
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    className={`mt-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : ''}`}
                    placeholder="A brief description..."
                    rows={3}
                  />
                </div>
              </div>

              {/* Social Links */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className={isDarkMode ? 'text-gray-200' : ''}>
                    Social Links
                  </Label>
                  <span className={`text-sm font-medium ${
                    validLinkCount >= 3 
                      ? 'text-green-600' 
                      : 'text-orange-600'
                  }`}>
                    {validLinkCount}/3 minimum
                    {validLinkCount >= 3 && <CheckCircle className="w-4 h-4 inline ml-1" />}
                  </span>
                </div>
                
                <div className="grid grid-cols-1 gap-3">
                  {socialLinkFields.map((field) => (
                    <div key={field.key}>
                      <Label htmlFor={field.key} className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {field.label}
                      </Label>
                      <Input
                        id={field.key}
                        data-testid={`scout-${field.key}-input`}
                        type="url"
                        value={formData[field.key]}
                        onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
                        className={`mt-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : ''}`}
                        placeholder={field.placeholder}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Important Notice about PR requirement */}
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-amber-900/40 border-amber-600' : 'bg-amber-50 border-amber-300'} border-2`}>
                <p className={`text-sm font-medium ${isDarkMode ? 'text-amber-200' : 'text-amber-800'}`}>
                  ⚠️ <strong>Important:</strong> You will need at least <strong>1 PR</strong> in your wallet to receive 10% of the respects earned by the claimable profile once claimed.
                </p>
                <p className={`text-sm mt-2 ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                  Please make sure you have it or click <strong>"Buy PR"</strong> @ Home page or @ Your Profile page.
                </p>
              </div>

              {/* Submit */}
              <div className="sticky bottom-0 pt-4 pb-2 bg-inherit -mx-6 px-6 sm:relative sm:mx-0 sm:px-0 sm:pb-0">
                <Button
                  type="submit"
                  data-testid="scout-submit-button"
                  disabled={isSubmitting || validLinkCount < 3}
                  className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-semibold py-3 text-base"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Star className="w-4 h-4 mr-2" />
                      Create Claimable Profile
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
